import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Container, Row, Col, Card, Spinner, Alert, Dropdown, Button } from "react-bootstrap";
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../context/PageTitleContext';
import { generateSlug } from '../utils/slugUtils';
import { OPEN_FILTERS, openFilterByKey, isOpenFilterActive, matchesOpenFilter } from '../utils/openStatus';
import { useSavedRestaurantIds } from '../hooks/useSavedRestaurantIds';
import RestaurantCard from './RestaurantCard';
import { ownershipStatus } from '../utils/ownership';

function RestaurantList() {
  const navigate = useNavigate();
  const { categoryId, cuisineId } = useParams();
  const location = useLocation();
  const { token } = useAuth(); // Get auth token for fetching user's pending restaurants
  const { setPageTitle } = usePageTitle();

  const [viewType, setViewType] = useState('restaurants');
  const [categories, setCategories] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [foods, setFoods] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCuisine, setSelectedCuisine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  // Location-based filtering state
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermission, setLocationPermission] = useState(null); // 'granted', 'denied', 'prompt'
  const [locationLoading, setLocationLoading] = useState(false);
  // #21: sort MODE, not a distance boolean. 'name' | 'distance' | 'recent'.
  // `sortByDistance` stays as a derived alias so the distance pipeline below reads
  // exactly as it did — the third option must not perturb #59/#165's behaviour.
  const [sortMode, setSortMode] = useState('name');
  const sortByDistance = sortMode === 'distance';
  const [distanceFilter, setDistanceFilter] = useState(null); // null, 1, 5, 10 (miles)
  const [restaurantsWithDistance, setRestaurantsWithDistance] = useState([]);
  // "Open now" filter (#62) — parity with the iOS toolbar toggle. Pure client-side
  // filter on the server-computed, timezone-aware is_open_now field (#39). Session-only.
  // #334: a duration, not a boolean. Key into OPEN_FILTERS; 'off' = no filter.
  const [openFilterKey, setOpenFilterKey] = useState('off');
  // Saved-state filter (#162): 'all' | 'favorites' | 'wishlist', plus #267's
  // 'yoursPrivate' | 'yoursAwaitingReview'. Narrows the list you're already
  // browsing — the thing neither the /saved page nor the category dropdown can do.
  // Client-side over useSavedRestaurantIds, so it works logged-out on guest saves
  // (#13) too. Session-only, like Open now.
  //
  // #267: the two "yours" values need no ownership test of their own. The list
  // endpoint is owner-aware — public rows plus YOUR OWN private ones — so a private
  // row that reached this client is by construction yours. Branching on
  // ownershipStatus() rather than on `visibility` is the point: #158 named the three
  // states precisely so a view can't collapse two of them, and "awaiting review" and
  // "just for me" are different questions the user is asking. iOS parity: the same
  // two values ship as SavedRestaurantFilter.yoursPrivate / .yoursAwaitingReview.
  const [savedFilter, setSavedFilter] = useState('all');
  // Favorite/Wishlist ids for badging list rows (#61) — account saves or guest saves.
  const { favoriteIds, wishlistIds } = useSavedRestaurantIds();
  // REMOVED: restaurantImages and foodImages state - now using images from API response directly

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  // Load saved location preferences from localStorage on mount
  useEffect(() => {
    try {
      // Load saved location
      const savedLocation = localStorage.getItem('dynerz_user_location');
      if (savedLocation) {
        const locationData = JSON.parse(savedLocation);
        // Check if location is less than 1 hour old
        if (Date.now() - locationData.timestamp < 60 * 60 * 1000) {
          setUserLocation({ lat: locationData.lat, lng: locationData.lng });
          setLocationPermission('granted');
        } else {
          // Location expired, remove it
          localStorage.removeItem('dynerz_user_location');
        }
      }

      // Load saved sort preference. #21 replaced the boolean key with a mode; read
      // the old one once so an existing "Distance" preference survives the upgrade
      // instead of silently resetting to A-Z.
      const savedMode = localStorage.getItem('dynerz_sort_mode');
      if (savedMode !== null) {
        setSortMode(['name', 'distance', 'recent'].includes(savedMode) ? savedMode : 'name');
      } else {
        const legacy = localStorage.getItem('dynerz_sort_by_distance');
        if (legacy !== null) setSortMode(legacy === 'true' ? 'distance' : 'name');
      }
    } catch (error) {
      console.error('Error loading saved location preferences:', error);
    }
  }, []);

  // Helper function to make authenticated fetch calls
  const authenticatedFetch = async (url, options = {}) => {
    const headers = {
      ...options.headers,
    };

    // Add Authorization header if user is logged in
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, { ...options, headers });
  };

  // Haversine formula to calculate distance between two points
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in miles
  };

  // Helper function to update sort preference and save to localStorage
  const updateSortPreference = (mode) => {
    setSortMode(mode);
    try {
      localStorage.setItem('dynerz_sort_mode', mode);
      // Keep the legacy key consistent rather than orphaned, so a stale tab or an
      // older cached bundle can't restore a contradictory preference.
      localStorage.setItem('dynerz_sort_by_distance', (mode === 'distance').toString());
    } catch (error) {
      console.warn('Failed to save sort preference to localStorage:', error);
    }
  };

  // Get user's current location
  const getUserLocation = () => {
    setLocationLoading(true);

    if (!navigator.geolocation) {
      setLocationPermission('denied');
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(location);
        setLocationPermission('granted');
        setLocationLoading(false);

        // Save to localStorage with timestamp
        try {
          localStorage.setItem('dynerz_user_location', JSON.stringify({
            lat: location.lat,
            lng: location.lng,
            timestamp: Date.now()
          }));
        } catch (error) {
          console.warn('Failed to save location to localStorage:', error);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationPermission('denied');
        setLocationLoading(false);
        // #165: distance sort is impossible without a location, so don't leave a
        // dead preference behind (it would restore as a ticked "Distance" on the
        // next visit over an alphabetical list). Clears the stored value too.
        updateSortPreference('name');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Selecting "Distance" needs a location fix; nudge for one if we don't have
  // it yet, then re-sort once it arrives (#59 — parity with the iOS Sort control).
  const selectDistanceSort = () => {
    if (!userLocation) {
      getUserLocation();
    }
    updateSortPreference('distance');
  };

  // REMOVED: fetchRestaurantImages, loadRestaurantImages, fetchFoodImages, loadFoodImages
  // Now using images directly from restaurant.images and food.image_url in API response

  // Calculate distances for all restaurants (instant - uses backend coordinates!)
  const calculateRestaurantDistances = (restaurantList) => {
    if (!userLocation) return restaurantList;

    const restaurantsWithDist = restaurantList.map(restaurant => {
      let minDistance = Infinity;
      let closestLocation = null;

      // Check all locations for this restaurant
      for (const location of restaurant.locations || []) {
        // Use lat/lng from backend (no geocoding needed!)
        if (location.latitude && location.longitude) {
          const distance = calculateDistance(
            userLocation.lat,
            userLocation.lng,
            location.latitude,
            location.longitude
          );
          if (distance < minDistance) {
            minDistance = distance;
            closestLocation = location;
          }
        }
      }

      return {
        ...restaurant,
        distance: minDistance === Infinity ? null : minDistance,
        closestLocation
      };
    });

    return restaurantsWithDist;
  };

  // Determine view type from URL
  const getViewTypeFromUrl = () => {
    if (location.pathname.includes('/food-category/')) return 'foods';
    if (location.pathname.includes('/cuisine/') && location.pathname.includes('/foods')) return 'foods';
    if (location.pathname.includes('/restaurant-category/')) return 'restaurants';
    if (location.pathname.includes('/cuisine/') && location.pathname.includes('/restaurants')) return 'restaurants';
    return 'restaurants'; // default
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setDataLoading(true);

    // Update URL to reflect selected category
    if (viewType === 'restaurants') {
      navigate(`/restaurant-category/${category.id}/${generateSlug(category.name)}`);
      // Fetch restaurants for the selected category
      authenticatedFetch(`${apiBaseUrl}/restaurants/?category_id=${category.id}`)
        .then((response) => response.json())
        .then((data) => {
          console.log(`Received restaurants for category ${category.name}:`, data);
          setRestaurants(data);
          setFoods([]);
          setDataLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching restaurants:", error);
          setDataLoading(false);
        });
    } else {
      navigate(`/food-category/${category.id}/${generateSlug(category.name)}`);
      // Fetch foods for the selected category
      fetch(`${apiBaseUrl}/foods/?category_id=${category.id}`)
        .then((response) => response.json())
        .then((data) => {
          console.log(`Received foods for category ${category.name}:`, data);
          setFoods(data);
          setRestaurants([]);
          setDataLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching foods:", error);
          setDataLoading(false);
        });
    }
  };

  const handleViewTypeChange = (newViewType) => {
    setViewType(newViewType);
    setSelectedCategory(null);
    setRestaurants([]);
    setFoods([]);
    navigate('/restaurants');
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setRestaurants([]);
    setFoods([]);
    navigate('/restaurants');
  };

  const handleFoodClick = (food) => {
    navigate(`/food/${food.id}/${generateSlug(food.name)}`);
  };

  // Update restaurants with distance when user location or restaurants change
  useEffect(() => {
    if (userLocation && restaurants.length > 0 && viewType === 'restaurants') {
      try {
        setDataLoading(true);

        // Calculate distances (instant - synchronous!)
        const restaurantsWithDist = calculateRestaurantDistances(restaurants);

        // Sort by distance or alphabetically
        let sortedRestaurants;
        if (sortByDistance) {
          sortedRestaurants = [...restaurantsWithDist].sort((a, b) => {
            if (a.distance === null) return 1;
            if (b.distance === null) return -1;
            return a.distance - b.distance;
          });
        } else {
          // Sort alphabetically when not sorting by distance
          sortedRestaurants = [...restaurantsWithDist].sort((a, b) =>
            a.name.localeCompare(b.name)
          );
        }

        // Apply distance filter if set
        if (distanceFilter) {
          sortedRestaurants = sortedRestaurants.filter(
            restaurant => restaurant.distance !== null && restaurant.distance <= distanceFilter
          );
        }

        setRestaurantsWithDistance(sortedRestaurants);
        setDataLoading(false);
      } catch (error) {
        console.error('Error calculating distances:', error);
        setDataLoading(false);
      }
    } else {
      setRestaurantsWithDistance(restaurants);
    }
  }, [userLocation, restaurants, sortByDistance, distanceFilter, viewType]);

  // Set page title based on selected category/cuisine
  useEffect(() => {
    if (selectedCategory) {
      setPageTitle(selectedCategory.name);
    } else if (selectedCuisine) {
      setPageTitle(selectedCuisine.name);
    } else {
      setPageTitle('All Restaurants');
    }
  }, [selectedCategory, selectedCuisine, setPageTitle]);

  // Update view type when URL changes
  useEffect(() => {
    const newViewType = getViewTypeFromUrl();
    setViewType(newViewType);
  }, [location.pathname]);

  // Handle direct category navigation from URL
  useEffect(() => {
    if (categoryId && categories.length > 0) {
      const category = categories.find(cat => cat.id === parseInt(categoryId));
      if (category) {
        // Update selected category and fetch data whenever categoryId changes
        setSelectedCategory(category);
        setDataLoading(true);

        if (viewType === 'restaurants') {
          authenticatedFetch(`${apiBaseUrl}/restaurants/?category_id=${category.id}`)
            .then((response) => response.json())
            .then((data) => {
              console.log(`Received restaurants for category ${category.name}:`, data);
              // Sort alphabetically by name
              const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
              setRestaurants(sortedData);
              setFoods([]);
              setDataLoading(false);
            })
            .catch((error) => {
              console.error("Error fetching restaurants:", error);
              setDataLoading(false);
            });
        } else {
          fetch(`${apiBaseUrl}/foods/?category_id=${category.id}`)
            .then((response) => response.json())
            .then((data) => {
              console.log(`Received foods for category ${category.name}:`, data);
              // Sort alphabetically by name
              const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
              setFoods(sortedData);
              setRestaurants([]);
              setDataLoading(false);
            })
            .catch((error) => {
              console.error("Error fetching foods:", error);
              setDataLoading(false);
            });
        }
      }
    }
  // `token` is a dependency because authenticatedFetch sends it and /restaurants/
  // is OWNER-AWARE: the same URL returns different rows signed in vs out. Without
  // it, signing in leaves your private/pending places missing until something else
  // forces a refetch, and signing out leaves them on screen. #267's filter is built
  // entirely out of those rows, so this is load-bearing, not hygiene.
  }, [categoryId, categories, viewType, apiBaseUrl, token]);

  // Handle direct cuisine navigation from URL
  useEffect(() => {
    if (cuisineId) {
      setDataLoading(true);

      // First fetch the cuisine details
      fetch(`${apiBaseUrl}/cuisines/${cuisineId}`)
        .then((response) => response.json())
        .then((cuisineData) => {
          setSelectedCuisine(cuisineData);
        })
        .catch((error) => {
          console.error("Error fetching cuisine:", error);
        });

      if (location.pathname.includes('/restaurants')) {
        authenticatedFetch(`${apiBaseUrl}/cuisines/${cuisineId}/restaurants`)
          .then((response) => response.json())
          .then((data) => {
            console.log(`Received restaurants for cuisine ${cuisineId}:`, data);
            // Sort alphabetically by name
            const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
            setRestaurants(sortedData);
            setFoods([]);
            setDataLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching restaurants by cuisine:", error);
            setDataLoading(false);
          });
      } else if (location.pathname.includes('/foods')) {
        fetch(`${apiBaseUrl}/cuisines/${cuisineId}/foods`)
          .then((response) => response.json())
          .then((data) => {
            console.log(`Received foods for cuisine ${cuisineId}:`, data);
            // Sort alphabetically by name
            const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
            setFoods(sortedData);
            setRestaurants([]);
            setDataLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching foods by cuisine:", error);
            setDataLoading(false);
          });
      }
    }
  // `token` — owner-aware endpoint, see the note on the category effect above.
  }, [cuisineId, location.pathname, apiBaseUrl, token]);

  // Fetch categories when viewType changes
  useEffect(() => {
    setLoading(true);
    const endpoint = viewType === 'restaurants'
      ? `${apiBaseUrl}/restaurants/categories/`
      : `${apiBaseUrl}/foods/categories/`;

    console.log(`Starting to fetch ${viewType} categories from:`, endpoint);

    fetch(endpoint)
      .then((response) => {
        console.log("Response received:", response);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log(`Received ${viewType} categories from API:`, data);
        // Filter out CLOSED category - now using is_closed field instead
        // (case-insensitive to catch all variations)
        const filteredCategories = data.filter(
          cat => cat.name.toUpperCase() !== 'CLOSED'
        );
        // Sort alphabetically by name for consistent display
        const sortedCategories = filteredCategories.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setCategories(sortedCategories);
        setLoading(false);
      })
      .catch((error) => {
        console.error(`Error fetching ${viewType} categories:`, error);
        console.error("Error type:", error.name);
        console.error("Error message:", error.message);
        console.log("API call failed - check if backend is running or CORS policy");
        setLoading(false);
      });
  }, [viewType, apiBaseUrl]);

  // Fetch all restaurants or foods on initial load (when no category/cuisine is selected)
  useEffect(() => {
    if (!categoryId && !cuisineId && !selectedCategory && !selectedCuisine) {
      setDataLoading(true);

      if (viewType === 'restaurants') {
        authenticatedFetch(`${apiBaseUrl}/restaurants/`)
          .then((response) => response.json())
          .then((data) => {
            console.log('Received all restaurants:', data);
            // Sort alphabetically by name
            const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
            setRestaurants(sortedData);
            setDataLoading(false);
          })
          .catch((error) => {
            console.error('Error fetching all restaurants:', error);
            setDataLoading(false);
          });
      } else {
        fetch(`${apiBaseUrl}/foods/`)
          .then((response) => response.json())
          .then((data) => {
            console.log('Received all foods:', data);
            // Sort alphabetically by name
            const sortedData = [...data].sort((a, b) => a.name.localeCompare(b.name));
            setFoods(sortedData);
            setDataLoading(false);
          })
          .catch((error) => {
            console.error('Error fetching all foods:', error);
            setDataLoading(false);
          });
      }
    }
  // `token` — owner-aware endpoint, see the note on the category effect above.
  }, [viewType, categoryId, cuisineId, selectedCategory, selectedCuisine, apiBaseUrl, token]);

  // Generate dynamic page title
  const getPageTitle = () => {
    if (selectedCuisine) {
      return viewType === 'restaurants'
        ? `${selectedCuisine.name} Restaurants`
        : `${selectedCuisine.name} Foods`;
    }
    if (selectedCategory) {
      return viewType === 'restaurants'
        ? `${selectedCategory.name} Restaurants`
        : `${selectedCategory.name} Foods`;
    }
    return viewType === 'restaurants' ? 'All Restaurants' : 'All Foods';
  };

  // Update document title
  useEffect(() => {
    document.title = `${getPageTitle()} | dyner.z`;
  }, [selectedCategory, selectedCuisine, viewType]);

  // Base list (distance-sorted when we have a fix), then the optional "Open now"
  // filter (#62), then the optional saved-state filter (#162). Both the empty-state
  // check and the grid render off this.
  const locatedList = userLocation ? restaurantsWithDistance : restaurants;
  // #21 "Newest": id descending. The id is a server-side serial, so descending id
  // IS the order places entered the catalogue — exact for every restaurant ever
  // added, which a "date added" field could not be (those dates were never
  // recorded). Applied HERE rather than in the distance pipeline above because
  // recency needs no location, so it must work before any fix arrives.
  const baseRestaurantList =
    sortMode === 'recent' ? [...locatedList].sort((a, b) => b.id - a.id) : locatedList;
  // #334: filter by how much longer a place stays open, not just whether it is.
  // staysOpenAtLeast owns the four-state logic — `minutes >= n` here would drop
  // every 24-hour place and let unknown hours through. 'openNow' passes 0, which
  // is exactly the old `is_open_now === true` set.
  const activeOpenFilter = openFilterByKey(openFilterKey);
  // #402 adds "open or opening within N", which matchesOpenFilter dispatches.
  const openFilterIsActive = isOpenFilterActive(activeOpenFilter);
  const openNowRestaurants = openFilterIsActive
    ? baseRestaurantList.filter((r) => matchesOpenFilter(r, activeOpenFilter))
    : baseRestaurantList;
  const savedIdsForFilter =
    savedFilter === 'favorites' ? favoriteIds : savedFilter === 'wishlist' ? wishlistIds : null;

  // #267: how many of the LOADED restaurants are the viewer's own, by state.
  // Counted off `restaurants` (the raw fetched list) rather than the filtered one,
  // so "you have none at all" stays distinguishable from "none survived Open now /
  // distance / category" — the two empty cases need different copy, same as #162.
  // Drives whether the menu section appears at all, so a signed-out browser never
  // sees it and this component needs no notion of auth. iOS: `ownCounts`.
  const ownCounts = restaurants.reduce(
    (acc, r) => {
      const key = ownershipStatus(r)?.key;
      if (key === 'awaitingApproval') acc.awaiting += 1;
      else if (key === 'justForMe' || key === 'madePrivate') acc.settled += 1;
      return acc;
    },
    { settled: 0, awaiting: 0 }
  );

  const displayedRestaurants = (() => {
    if (savedIdsForFilter) return openNowRestaurants.filter((r) => savedIdsForFilter.has(r.id));
    // #267. "Private" deliberately covers BOTH settled private states: an admin
    // unpublishing your restaurant (rejected) and your own "just for me" add are
    // different explanations, but they answer the same browsing question — nothing
    // is expected of you, and only you can see it. "Awaiting approval" is the one
    // that is still in flight, which is why it is its own value.
    if (savedFilter === 'yoursPrivate') {
      return openNowRestaurants.filter((r) => {
        const key = ownershipStatus(r)?.key;
        return key === 'justForMe' || key === 'madePrivate';
      });
    }
    if (savedFilter === 'yoursAwaitingReview') {
      return openNowRestaurants.filter((r) => ownershipStatus(r)?.key === 'awaitingApproval');
    }
    return openNowRestaurants;
  })();

  // The button always reads "Filter" (iOS parity — its Filter button never changes
  // text); active state is carried by the filled styling alone. It reflects its own
  // state only (category + saved), not Open now or Sort, which have their own
  // visible controls.
  const filterIsActive = savedFilter !== 'all' || Boolean(selectedCategory);

  // #165: what the Sort menu shows must be the EFFECTIVE sort, not the stored
  // intent. The rendered list falls back to alphabetical whenever there's no
  // location (see baseRestaurantList), but `sortByDistance` persists in
  // localStorage with no expiry while the cached location expires after an hour
  // — so intent alone would tick "Distance" over an obviously A–Z list.
  const distanceSortActive = sortByDistance && Boolean(userLocation);
  // Distance was asked for and we're still waiting on the browser/GPS.
  const distanceSortPending = sortByDistance && !userLocation && locationLoading;
  // #21: A-Z can no longer be inferred as "not distance" — that would tick it while
  // Newest was active. It is the effective sort when chosen, and also when Distance
  // was asked for but no location arrived (the #165 fallback the list actually renders).
  const alphabeticalActive = sortMode === 'name' || (sortByDistance && !userLocation);

  // The saved filter has two distinct empty cases and they need different copy:
  // you've saved nothing at all, vs. you've saved things but none are in this list.
  const savedFilterLabel = savedFilter === 'favorites' ? 'favorites' : 'wishlist';
  const hasNoSavesAtAll = savedIdsForFilter !== null && savedIdsForFilter.size === 0;

  const emptyStateMessage = () => {
    // #267: two empty cases per value, same as #162 gave the saved filter —
    // "you have none at all" reads differently from "none are in this list".
    if (savedFilter === 'yoursPrivate') {
      if (ownCounts.settled === 0) {
        return 'You haven\'t added any private places yet — add a restaurant and choose "Keep private" to see it here.';
      }
      return openFilterIsActive
        ? `None of your private places are ${activeOpenFilter.emptyStateFragment}.`
        : 'None of your private places are in this list.';
    }
    if (savedFilter === 'yoursAwaitingReview') {
      if (ownCounts.awaiting === 0) return 'Nothing of yours is awaiting approval.';
      return openFilterIsActive
        ? `None of your submissions are ${activeOpenFilter.emptyStateFragment}.`
        : 'None of your submissions are in this list.';
    }
    if (savedIdsForFilter) {
      if (hasNoSavesAtAll) {
        return savedFilter === 'favorites'
          ? "You haven't favorited any restaurants yet — tap the ♥ on a restaurant to add one."
          : "Your wishlist is empty — tap the 📌 on a restaurant to add one.";
      }
      return openFilterIsActive
        ? `None of your ${savedFilterLabel} are ${activeOpenFilter.emptyStateFragment}.`
        : `None of your ${savedFilterLabel} are in this list.`;
    }
    if (openFilterIsActive) {
      return `Nothing ${activeOpenFilter.emptyStateFragment} — choose "Any time" to see all restaurants.`;
    }
    if (distanceFilter) {
      return `No restaurants found within ${distanceFilter} mile${distanceFilter > 1 ? 's' : ''} of your location.`;
    }
    return 'No restaurants found in this category.';
  };

  return (
    <Container fluid className="restaurant-list" style={{ padding: "20px" }}>
      {/* First-class Alphabetical ↔ Distance sort (#59) — a single always-visible
          control, parity with the iOS app. Picking Distance requests location. */}
      {viewType === 'restaurants' && (
        <div className="mb-4 d-flex gap-2 align-items-center flex-wrap justify-content-end location-controls">
          {/* Open-hours filter (#62, #334) — parity with the iOS toolbar. A
              dropdown rather than a toggle, because "open now" is not the question
              when you are deciding whether to set out: a place shutting in ten
              minutes is open and useless. Built like the Sort control beside it
              (active + ✓) so the three header controls read as one set. */}
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              id="restaurant-open-hours"
              style={{
                backgroundColor: openFilterIsActive ? 'var(--dynerz-slate-blue-dark)' : 'white',
                color: openFilterIsActive ? 'white' : 'var(--dynerz-slate-blue-dark)',
                border: '2px solid var(--dynerz-slate-blue-dark)',
                fontWeight: '500'
              }}
            >
              {openFilterIsActive ? '✓ ' : '🕒 '}{activeOpenFilter.chipLabel}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {OPEN_FILTERS.map((option) => (
                <React.Fragment key={option.key}>
                  {option.dividerBefore && <Dropdown.Divider />}
                  <Dropdown.Item
                    active={option.key === openFilterKey}
                    onClick={() => setOpenFilterKey(option.key)}
                  >
                    {option.key === openFilterKey ? '✓ ' : ''}{option.menuLabel}
                  </Dropdown.Item>
                </React.Fragment>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              id="restaurant-sort"
              style={{
                backgroundColor: 'white',
                color: 'var(--dynerz-slate-blue-dark)',
                border: '2px solid var(--dynerz-slate-blue-dark)',
                fontWeight: '500'
              }}
            >
              {locationLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Locating…
                </>
              ) : (
                <>⇅ Sort</>
              )}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {/* #165: ticks follow distanceSortActive (intent AND a location),
                  so the menu can never claim Distance over an alphabetical list. */}
              <Dropdown.Item active={alphabeticalActive} onClick={() => updateSortPreference('name')}>
                {alphabeticalActive ? '✓ ' : ''}A–Z
              </Dropdown.Item>
              <Dropdown.Item active={distanceSortActive} onClick={selectDistanceSort}>
                {distanceSortActive ? '✓ ' : ''}Distance
                {distanceSortPending && (
                  <span className="text-muted ms-1">(locating…)</span>
                )}
              </Dropdown.Item>
              {/* #21: needs no location, so it is never pending and never falls back. */}
              <Dropdown.Item active={sortMode === 'recent'} onClick={() => updateSortPreference('recent')}>
                {sortMode === 'recent' ? '✓ ' : ''}Newest
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>

          {/* Filter (#162) — categories + saved state in one control. Sits LAST to
              match the iOS list header, which orders Open now · Map · Sort · Filter
              (web has no Map). Categories navigate to the existing
              /restaurant-category routes (keeping web's shareable, SEO-valuable
              category URLs) while the saved options filter the loaded list in
              place, so the two still compose: "my favorites, within Italian". */}
          <Dropdown>
            <Dropdown.Toggle
              size="sm"
              id="restaurant-filter"
              style={{
                backgroundColor: filterIsActive ? 'var(--dynerz-slate-blue-dark)' : 'white',
                color: filterIsActive ? 'white' : 'var(--dynerz-slate-blue-dark)',
                border: '2px solid var(--dynerz-slate-blue-dark)',
                fontWeight: '500'
              }}
            >
              ☰ Filter
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Dropdown.Header>Saved</Dropdown.Header>
              <Dropdown.Item active={savedFilter === 'all'} onClick={() => setSavedFilter('all')}>
                {savedFilter === 'all' ? '✓ ' : ''}All restaurants
              </Dropdown.Item>
              <Dropdown.Item
                active={savedFilter === 'favorites'}
                onClick={() => setSavedFilter('favorites')}
              >
                {savedFilter === 'favorites' ? '✓ ' : ''}♥ My Favorites
                {favoriteIds.size > 0 && (
                  <span className="text-muted ms-1">({favoriteIds.size})</span>
                )}
              </Dropdown.Item>
              <Dropdown.Item
                active={savedFilter === 'wishlist'}
                onClick={() => setSavedFilter('wishlist')}
              >
                {savedFilter === 'wishlist' ? '✓ ' : ''}📌 My Wishlist
                {wishlistIds.size > 0 && (
                  <span className="text-muted ms-1">({wishlistIds.size})</span>
                )}
              </Dropdown.Item>

              {/* #267: your own places. Shown only when the loaded list actually
                  contains some — a signed-out browser never sees this section, and
                  neither does a signed-in user who has added nothing, because the
                  owner-aware list simply has no such rows for them. That is why this
                  component needs no notion of auth. Each item hides on its own count
                  for the same reason (iOS parity). */}
              {(ownCounts.settled > 0 || ownCounts.awaiting > 0) && (
                <>
                  <Dropdown.Divider />
                  <Dropdown.Header>Yours</Dropdown.Header>
                  {ownCounts.settled > 0 && (
                    <Dropdown.Item
                      active={savedFilter === 'yoursPrivate'}
                      onClick={() => setSavedFilter('yoursPrivate')}
                    >
                      {savedFilter === 'yoursPrivate' ? '✓ ' : ''}🔒 Private
                      <span className="text-muted ms-1">({ownCounts.settled})</span>
                    </Dropdown.Item>
                  )}
                  {ownCounts.awaiting > 0 && (
                    <Dropdown.Item
                      active={savedFilter === 'yoursAwaitingReview'}
                      onClick={() => setSavedFilter('yoursAwaitingReview')}
                    >
                      {savedFilter === 'yoursAwaitingReview' ? '✓ ' : ''}⏳ Awaiting approval
                      <span className="text-muted ms-1">({ownCounts.awaiting})</span>
                    </Dropdown.Item>
                  )}
                </>
              )}

              <Dropdown.Divider />
              <Dropdown.Header>Category</Dropdown.Header>
              <Dropdown.Item active={!selectedCategory} onClick={handleBackToCategories}>
                {!selectedCategory ? '✓ ' : ''}All categories
              </Dropdown.Item>
              {categories.map((category) => (
                <Dropdown.Item
                  key={category.id}
                  active={selectedCategory?.id === category.id}
                  onClick={() => handleCategoryClick(category)}
                >
                  {selectedCategory?.id === category.id ? '✓ ' : ''}{category.name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

        </div>
      )}

      {/* #164: location status lives on its OWN row, deliberately NOT inside the
          control bar above. Both of these render asynchronously (geolocation
          resolves whenever it resolves), and inside a justify-content-end flex
          row they landed to the RIGHT of the buttons and shoved them ~256px left
          mid-session — the "buttons drift left at some random point" report.
          Keeping them out of that row anchors the buttons permanently. */}
      {viewType === 'restaurants' && (userLocation || locationPermission === 'denied') && (
        <div className="mb-4 d-flex justify-content-end">
          {userLocation && (
            <small style={{ color: 'var(--dynerz-slate-blue-dark)', fontWeight: '500' }}>
              Showing distances from your location
            </small>
          )}
          {locationPermission === 'denied' && (
            <Alert variant="warning" className="mb-0 py-2 px-3">
              Location access denied. Enable location in your browser settings to sort by distance.
            </Alert>
          )}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "60vh" }}>
          <Spinner animation="border" />
        </div>
      ) : (
        <div>
          {/* Show restaurants or foods list */}
          <div>
              {dataLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "40vh" }}>
                  <Spinner animation="border" />
                </div>
              ) : (
                <div>
                  {viewType === 'restaurants' ? (
                    // Restaurant listing
                    displayedRestaurants.length === 0 ? (
                      <div className="text-center py-5">
                        <p className="text-muted">{emptyStateMessage()}</p>
                        {savedIdsForFilter && (
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => setSavedFilter('all')}
                          >
                            Show all restaurants
                          </Button>
                        )}
                      </div>
                    ) : (
                    <Row>
                      {displayedRestaurants.map((restaurant) => (
                        <Col key={restaurant.id} xs={12} sm={6} lg={4} xl={3} className="mb-3">
                          <RestaurantCard
                            restaurant={restaurant}
                            favoriteIds={favoriteIds}
                            wishlistIds={wishlistIds}
                            userLocation={userLocation}
                          />
                        </Col>
                      ))}
                    </Row>
                    )
                  ) : (
                    // Food listing
                    foods.length === 0 ? (
                      <div className="text-center py-5">
                        <p className="text-muted">No foods found in this category.</p>
                      </div>
                    ) : (
                      <Row>
                        {foods.map((food) => {
                          // Get cuisines for display (limit to 2)
                          const cuisines = food.cuisines?.slice(0, 2).map(c => c.name).join(' • ') || '';
                          // Build dietary info
                          const dietaryBadges = [];
                          if (food.is_vegetarian) dietaryBadges.push('🥬 Vegetarian');
                          if (food.is_vegan) dietaryBadges.push('🌱 Vegan');
                          if (food.is_gluten_free) dietaryBadges.push('🌾 GF');
                          const dietaryText = dietaryBadges.join(' • ');

                          return (
                            <Col key={food.id} xs={12} sm={6} lg={4} xl={3} className="mb-3">
                              <Card
                                className="shadow-sm food-card"
                                style={{ cursor: "pointer", height: "100%" }}
                                onClick={() => handleFoodClick(food)}
                              >
                                <Card.Body className="p-3">
                                  {/* Title and Rating Row */}
                                  <div className="d-flex justify-content-between align-items-start mb-1">
                                    <h6 className="mb-0 fw-bold text-primary" style={{ fontSize: "1rem" }}>
                                      {food.name}
                                    </h6>
                                    {/* Placeholder for rating - will show once reviews are loaded */}
                                    <small className="text-muted">⭐ -</small>
                                  </div>

                                  {/* Cuisine and Dietary Info */}
                                  <div className="text-muted small mb-1">
                                    {cuisines && <span>{cuisines}</span>}
                                    {cuisines && dietaryText && <span> • </span>}
                                    {dietaryText && <span>{dietaryText}</span>}
                                  </div>

                                  {/* Price and calories */}
                                  <div className="small mb-2">
                                    {food.base_price && (
                                      <span className="text-success fw-bold">${Number(food.base_price).toFixed(2)}</span>
                                    )}
                                    {food.base_price && food.calories && <span className="text-muted"> • </span>}
                                    {food.calories && (
                                      <span className="text-muted">{food.calories} cal</span>
                                    )}
                                  </div>

                                  {/* Single Food Image */}
                                  <div className="d-flex gap-2 overflow-hidden" style={{ height: "100px" }}>
                                    {food.image_url ? (
                                      <div className="border rounded flex-fill overflow-hidden">
                                        <img
                                          src={food.image_url}
                                          alt={food.name}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover"
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = '<div class="d-flex align-items-center justify-content-center h-100 text-muted small">📷</div>';
                                          }}
                                        />
                                      </div>
                                    ) : (
                                      // Placeholder if no image available
                                      <div className="bg-light border rounded flex-fill">
                                        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                                          🍽️
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    )
                  )}
                </div>
              )}
            </div>
        </div>
      )}
    </Container>
  );
}

export default RestaurantList;